import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Heart, Star, Award, ArrowRight, Users, MapPin, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import CornerOrnament from '../components/CornerOrnament';
import SEO from '../components/SEO';
import { useContent } from '../lib/content';
import Editable from '../components/Editable';
import EditableImage from '../components/EditableImage';

const VALUE_ICONS = [Shield, Heart, Star, Award];
const STAT_ICONS = [MapPin, Home, Star, Users];

export default function AboutPage() {
  const { get } = useContent('about');
  return (
    <div data-testid="about-page" className="bg-[#101010]">
      <SEO title="About Us — Our Story & Values" path="/about" description={get('hero_description')} />
      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-gold text-[11px] uppercase tracking-[0.4em] font-bold mb-5"><Editable page="about" field="hero_label">{get('hero_label')}</Editable></motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Editable page="about" field="hero_title" as="h1" className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-bold mb-5">{get('hero_title')}</Editable>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Editable page="about" field="hero_description" as="p" className="text-white/45 text-base sm:text-lg max-w-2xl mx-auto">{get('hero_description')}</Editable>
          </motion.div>
        </div>
      </section>

      {get('body') && (
        <section className="pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: get('body') }} />
          </div>
        </section>
      )}

      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Editable page="about" field="who_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-4">{get('who_label')}</Editable>
              <Editable page="about" field="who_title" as="h2" className="font-heading text-4xl text-white font-bold mb-6 leading-tight">{get('who_title')}</Editable>
              <Editable page="about" field="who_description1" as="p" className="text-white/50 leading-relaxed mb-6">{get('who_description1')}</Editable>
              <Editable page="about" field="who_description2" as="p" className="text-white/50 leading-relaxed">{get('who_description2')}</Editable>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative rounded-xl overflow-hidden aspect-[4/5]">
              <EditableImage page="about" field="who_image" src={get('who_image')} alt="About Plush Residence" className="w-full h-full object-cover" />
              <CornerOrnament size={28} offset="12px" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#161616]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((n, i) => {
              const Icon = STAT_ICONS[i];
              return (
                <motion.div key={n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                  <Icon className="w-6 h-6 text-gold mx-auto mb-3" />
                  <Editable page="about" field={`stat${n}_value`} as="p" className="text-3xl sm:text-4xl font-bold text-white mb-1">{get(`stat${n}_value`)}</Editable>
                  <Editable page="about" field={`stat${n}_label`} as="p" className="text-[10px] text-white/35 uppercase tracking-widest">{get(`stat${n}_label`)}</Editable>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Editable page="about" field="values_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-3">{get('values_label')}</Editable>
            <Editable page="about" field="values_title" as="h2" className="font-heading text-4xl text-white font-bold">{get('values_title')}</Editable>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((n, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <motion.div key={n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="group relative bg-[#1c1c1c] rounded-xl p-8 border border-white/[0.08] hover:border-gold/30 transition-all duration-500">
                  <CornerOrnament size={14} offset="8px" />
                  <div className="w-14 h-14 rounded-xl bg-white/[0.04] group-hover:bg-gold flex items-center justify-center mb-5 transition-colors duration-500">
                    <Icon className="w-6 h-6 text-white/55 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <Editable page="about" field={`value${n}_title`} as="h3" className="font-heading text-lg font-bold text-white mb-2 group-hover:text-gold transition-colors duration-500">{get(`value${n}_title`)}</Editable>
                  <Editable page="about" field={`value${n}_text`} as="p" className="text-sm text-white/45 leading-relaxed">{get(`value${n}_text`)}</Editable>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#161616]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Editable page="about" field="cta_title" as="h2" className="font-heading text-3xl sm:text-4xl text-white font-bold mb-4">{get('cta_title')}</Editable>
          <Editable page="about" field="cta_description" as="p" className="text-white/45 mb-8 max-w-md mx-auto">{get('cta_description')}</Editable>
          <Link to="/locations">
            <Button className="bg-gold hover:bg-gold-dark text-white h-13 px-8 text-xs uppercase tracking-[0.15em] font-semibold rounded-full transition-all duration-300 shadow-lg shadow-gold/20">
              <Editable page="about" field="cta_btn">{get('cta_btn')}</Editable> <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
